/**
 * Palete pentru câmpurile meteo afișate în secțiunile 3D.
 *
 * Spre deosebire de poluanți, unde aerul curat trebuie să rămână translucid,
 * câmpurile meteo sunt informative pe tot domeniul lor: o secțiune de
 * temperatură e utilă exact pentru că se vede peste tot. De aceea opacitatea
 * variază puțin, iar culoarea poartă toată informația.
 */

/**
 * Scară termică ancorată în punctul de îngheț.
 *
 * 0 °C e aproape alb, ca izoterma de 0° — nivelul de îngheț — să se citească
 * dintr-o privire în secțiunea verticală.
 */
const TEMP_STOPS = [
  { v: -60, c: [ 40,  16,  70] },   // stratosferă joasă: violet închis
  { v: -45, c: [ 60,  50, 165] },
  { v: -30, c: [ 45, 120, 215] },
  { v: -15, c: [ 90, 190, 240] },
  { v:   0, c: [230, 245, 255] },   // izoterma de 0 °C
  { v:  10, c: [130, 215, 130] },
  { v:  20, c: [245, 225,  90] },
  { v:  30, c: [242, 140,  50] },
  { v:  40, c: [198,  40,  40] },
];

function interpolate(stops, value) {
  const last = stops.length - 1;
  if (value <= stops[0].v) return [...stops[0].c];
  if (value >= stops[last].v) return [...stops[last].c];

  let i = 0;
  while (i < last && value > stops[i + 1].v) i++;
  const lo = stops[i], hi = stops[i + 1];
  const f = (value - lo.v) / (hi.v - lo.v);
  return [
    Math.round(lo.c[0] + (hi.c[0] - lo.c[0]) * f),
    Math.round(lo.c[1] + (hi.c[1] - lo.c[1]) * f),
    Math.round(lo.c[2] + (hi.c[2] - lo.c[2]) * f),
  ];
}

/**
 * Temperatura → [r, g, b, a], cu alfa în 0–255.
 * @param {number} degC
 */
export function tempColorRGBA(degC) {
  if (degC == null || isNaN(degC)) return [0, 0, 0, 0];
  const [r, g, b] = interpolate(TEMP_STOPS, degC);
  // Aproape opac: câmpul e semnificativ peste tot, nu doar în extreme.
  return [r, g, b, Math.round(0.88 * 255)];
}

/** Repere pentru legendă: capetele scării și punctul de îngheț. */
export const TEMP_RANGE = { min: -60, max: 40, unit: '°C' };

/**
 * Umiditate relativă, cu opacitate crescătoare.
 *
 * Aici opacitatea poartă informația la fel de mult ca și culoarea: aerul uscat
 * rămâne aproape invizibil, iar peste ~85% pânza se închide brusc, așa că
 * straturile de nori apar ca benzi distincte în secțiunea verticală. O scară
 * cu opacitate constantă ar fi arătat o suprafață uniformă din care nu se
 * putea citi unde e norul.
 */
const HUMIDITY_STOPS = [
  { v:   0, c: [150, 105,  60], a: 0.04 },   // foarte uscat: maro abia vizibil
  { v:  25, c: [175, 150, 105], a: 0.10 },
  { v:  45, c: [200, 200, 190], a: 0.18 },
  { v:  65, c: [165, 205, 230], a: 0.34 },
  { v:  80, c: [180, 225, 245], a: 0.58 },
  { v:  90, c: [225, 243, 252], a: 0.82 },   // prag de formare a norilor
  { v: 100, c: [255, 255, 255], a: 0.95 },
];

export function humidityColorRGBA(pct) {
  if (pct == null || isNaN(pct)) return [0, 0, 0, 0];
  const stops = HUMIDITY_STOPS;
  const last = stops.length - 1;

  let lo, hi, f;
  if (pct <= stops[0].v) { lo = hi = stops[0]; f = 0; }
  else if (pct >= stops[last].v) { lo = hi = stops[last]; f = 0; }
  else {
    let i = 0;
    while (i < last && pct > stops[i + 1].v) i++;
    lo = stops[i]; hi = stops[i + 1];
    f = (pct - lo.v) / (hi.v - lo.v);
  }

  return [
    Math.round(lo.c[0] + (hi.c[0] - lo.c[0]) * f),
    Math.round(lo.c[1] + (hi.c[1] - lo.c[1]) * f),
    Math.round(lo.c[2] + (hi.c[2] - lo.c[2]) * f),
    Math.round((lo.a + (hi.a - lo.a) * f) * 255),
  ];
}

export const HUMIDITY_RANGE = { min: 0, max: 100, unit: '%' };

/** Rampa CSS, cu alfa inclus — bara de legendă arată și cât de opacă e pânza. */
export function humidityGradientCss() {
  const stops = HUMIDITY_STOPS.map(s =>
    `rgba(${s.c[0]},${s.c[1]},${s.c[2]},${s.a.toFixed(2)}) ${s.v}%`
  );
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/** Rampa CSS a paletei termice, pentru bara de legendă. */
export function tempGradientCss() {
  const stops = TEMP_STOPS.map(s => {
    const pct = ((s.v - TEMP_RANGE.min) / (TEMP_RANGE.max - TEMP_RANGE.min)) * 100;
    return `rgb(${s.c[0]},${s.c[1]},${s.c[2]}) ${pct.toFixed(1)}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}
